import type { EnumDef } from '@clawhire/shared';
import { cn } from '@/lib/utils';
import { enumLabel } from '@/lib/format';
import { TONE_CLASSES, TONE_DOT_CLASSES, toneFor, type ToneMap } from '@/components/data/tones';

export interface EnumBadgeProps<T extends string> {
  def: EnumDef<T>;
  value: T | string | null | undefined;
  /** Override the colour map (code -> tone); defaults come from `tones.ts`. */
  tones?: ToneMap;
  className?: string;
  /** Show a coloured dot before the label. */
  dot?: boolean;
  size?: 'sm' | 'md';
}

/**
 * Coloured badge for a stage/status enum, labelled from `@clawhire/shared`:
 *   <EnumBadge def={PositionStage} value={position.stage} />
 */
export function EnumBadge<T extends string>({
  def,
  value,
  tones,
  className,
  dot = false,
  size = 'md',
}: EnumBadgeProps<T>) {
  const tone = toneFor(def, value, tones);
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border font-medium',
        size === 'sm' ? 'px-1.5 py-0 text-[11px] leading-5' : 'px-2 py-0.5 text-xs leading-5',
        TONE_CLASSES[tone],
        className,
      )}
    >
      {dot ? (
        <span className={cn('h-1.5 w-1.5 rounded-full', TONE_DOT_CLASSES[tone])} aria-hidden="true" />
      ) : null}
      {enumLabel(def, value)}
    </span>
  );
}
